import { useMemo } from 'react';
import { VolatilitySurface3D as VolatilitySurface3DType } from 'types/moviefone';
import { ProjectedSurface } from 'gen/stream-api/requests';
import { buildCubicSpline } from 'util/spline';
import { snapToListedStrikes } from 'components/optionsCalculator/pnlChart/heatmapMetrics';

export const MIN_IV = 0.01;
export const IV_CAP = 3.0;

// Shared by VolatilityChartPanel and both chart-type renderers (surface,
// heatmap) it switches between.
export type SurfaceView = 'current' | 'projected' | 'gap';

export type StrikeDensity = 'sparse' | 'normal' | 'dense';
// 'listed' snaps the evenly spaced rows to the nearest listed strike.
export type StrikeSpacing = 'even' | 'listed';
export type ExpirationDensity = 'fewer' | 'normal' | 'more';
export type PruneLevel = 'none' | 'low' | 'medium' | 'high';

// Same thresholds as the real Volatility Dashboard's Fixed Strike Matrix prune
// settings: minimum fraction of real (non-NaN) cells a row/column must keep.
const PRUNE_THRESHOLDS: Record<PruneLevel, number> = {
  none: 0,
  low: 0.25,
  medium: 0.5,
  high: 0.75,
};

// Never prune below these - a near-empty chart isn't more useful than a sparse one.
const MIN_PRUNED_EXPIRATIONS = 3;
const MIN_PRUNED_STRIKES = 5;

const STRIKE_COUNTS: Record<StrikeDensity, number> = {
  sparse: 12,
  normal: 20,
  dense: 32,
};

// 'fewer' prunes to this count; 'more' interpolates extra columns, capped here.
const FEWER_EXPIRATION_COUNT = 6;
const MORE_EXPIRATION_CAP = 24;

export interface VolSurfaceGrids {
  strikeGrid: number[];
  dteValues: number[];
  /** Expiration date per dteValues entry; null for interpolated columns. */
  expirationDates: (string | null)[];
  currentZValues: number[][];
  projectedZValues: number[][];
  gapZValues: number[][];
}

// Evenly-spaced index subsample, keeping the first and last item.
const subsampleByCount = <T>(items: T[], count: number): T[] => {
  if (count >= items.length) {
    return items;
  }
  if (count <= 1) {
    return [items[0]];
  }
  return Array.from(
    { length: count },
    (_, i) => items[Math.round((i * (items.length - 1)) / (count - 1))],
  );
};

// Inserts a midpoint between each real DTE pair; reals always survive the cap.
const buildDenseDtes = (realDtes: number[], maxTotal: number): number[] => {
  const dense: number[] = [];
  for (let i = 0; i < realDtes.length; i++) {
    dense.push(realDtes[i]);
    if (i < realDtes.length - 1) {
      const midpoint = Math.round((realDtes[i] + realDtes[i + 1]) / 2);
      if (midpoint > realDtes[i] && midpoint < realDtes[i + 1]) {
        dense.push(midpoint);
      }
    }
  }
  return dense.length > maxTotal
    ? [
        ...realDtes,
        ...subsampleByCount(
          dense.filter((d) => !realDtes.includes(d)),
          maxTotal - realDtes.length,
        ),
      ].sort((a, b) => a - b)
    : dense;
};

// Per-strike-column spline across DTE, so an in-between target interpolates instead of repeating the nearest real column.
const interpolateAcrossDtes = (
  realDtes: number[],
  realRows: number[][],
  targetDtes: number[],
  strikeCount: number,
): number[][] => {
  if (realDtes.length < 2) {
    const fallback = realRows[0] ?? new Array<number>(strikeCount).fill(NaN);
    return targetDtes.map(() => fallback);
  }

  const minDte = Math.min(...realDtes);
  const maxDte = Math.max(...realDtes);
  const columnSplines = Array.from({ length: strikeCount }, (_, strikeIdx) => {
    const points = realDtes
      .map((dte, i): [number, number] => [dte, realRows[i][strikeIdx]])
      .filter(([, v]) => !Number.isNaN(v));
    if (points.length < 2) {
      return null;
    }
    return buildCubicSpline(
      points.map(([dte]) => dte),
      points.map(([, v]) => v),
    );
  });

  return targetDtes.map((dte) =>
    columnSplines.map((spline) => {
      if (spline == null || dte < minDte || dte > maxDte) {
        return NaN;
      }
      return spline(dte);
    }),
  );
};

// Shared by VolatilitySurface3D (3D surface) and VolatilityHeatmap (Fixed
// Strike Matrix view) - both render the same underlying grid, just with
// different Plotly trace types and axis orientations.
export const useVolSurfaceGrids = (
  surfaceData: VolatilitySurface3DType,
  projectedSurface?: ProjectedSurface | null,
  strikeDensity: StrikeDensity = 'normal',
  expirationDensity: ExpirationDensity = 'normal',
  pruneExpirations: PruneLevel = 'none',
  pruneStrikes: PruneLevel = 'none',
  strikeSpacing: StrikeSpacing = 'even',
): VolSurfaceGrids | null =>
  useMemo(() => {
    if (surfaceData.expirations.length === 0) {
      return null;
    }

    const expirationsList = surfaceData.expirations;
    const realDtes = expirationsList.map((exp) => exp.dte);
    const strikeCount = STRIKE_COUNTS[strikeDensity];

    // Find global min/max strike across all expirations
    let globalMinStrike = Infinity;
    let globalMaxStrike = -Infinity;
    expirationsList.forEach((exp) => {
      exp.strikes.forEach((s) => {
        if (s < globalMinStrike) {
          globalMinStrike = s;
        }
        if (s > globalMaxStrike) {
          globalMaxStrike = s;
        }
      });
    });

    // Build common strike grid: evenly-spaced points between min and max
    const evenStrikeGrid: number[] = [];
    for (let i = 0; i < strikeCount; i++) {
      evenStrikeGrid.push(
        globalMinStrike +
          (i / (strikeCount - 1)) * (globalMaxStrike - globalMinStrike),
      );
    }
    const strikeGrid =
      strikeSpacing === 'listed'
        ? snapToListedStrikes(
            evenStrikeGrid,
            expirationsList.flatMap((exp) => exp.strikes),
          )
        : evenStrikeGrid;

    // Projected only gives us an ATM IV target per real listed expiration
    // (matched by dte, same as the term structure chart) - not a per-strike
    // smile. So "Projected" is today's real smile shifted by that single
    // ATM delta, and "Gap" is just that delta (flat across strikes).
    const projectedAtmByDte = new Map<number, number>(
      (projectedSurface?.projected_expirations ?? []).map((exp) => [
        exp.dte,
        exp.atm_iv,
      ]),
    );

    const realCurrentRows: number[][] = [];
    const realProjectedRows: number[][] = [];
    const realGapRows: number[][] = [];

    for (const exp of expirationsList) {
      // Need >= 2 unique strikes for spline
      if (exp.strikes.length < 2) {
        realCurrentRows.push(strikeGrid.map(() => NaN));
        realProjectedRows.push(strikeGrid.map(() => NaN));
        realGapRows.push(strikeGrid.map(() => NaN));
        continue;
      }
      // buildCubicSpline flat-clamps outside its domain (returns the edge
      // point's own IV), so a narrow-range expiration would otherwise paint
      // that one real edge value across every strike the global grid
      // extends beyond it. Mask those points to NaN instead.
      const expMinStrike = Math.min(...exp.strikes);
      const expMaxStrike = Math.max(...exp.strikes);
      const spline = buildCubicSpline(exp.strikes, exp.ivs);
      const currentRow = strikeGrid.map((strike) =>
        strike < expMinStrike || strike > expMaxStrike
          ? NaN
          : Math.min(spline(strike), IV_CAP),
      );
      realCurrentRows.push(currentRow);

      const projectedAtm = projectedAtmByDte.get(exp.dte);
      if (projectedAtm == null) {
        realProjectedRows.push(strikeGrid.map(() => NaN));
        realGapRows.push(strikeGrid.map(() => NaN));
        continue;
      }
      const shift = projectedAtm - exp.atm_iv;
      realProjectedRows.push(
        currentRow.map((iv) => Math.min(Math.max(iv + shift, MIN_IV), IV_CAP)),
      );
      realGapRows.push(
        currentRow.map((iv) => (Number.isNaN(iv) ? NaN : shift)),
      );
    }

    let dteValues = realDtes;
    let currentZValues = realCurrentRows;
    let projectedZValues = realProjectedRows;
    let gapZValues = realGapRows;

    if (
      expirationDensity === 'fewer' &&
      realDtes.length > FEWER_EXPIRATION_COUNT
    ) {
      const keepIndices = subsampleByCount(
        realDtes.map((_, i) => i),
        FEWER_EXPIRATION_COUNT,
      );
      dteValues = keepIndices.map((i) => realDtes[i]);
      currentZValues = keepIndices.map((i) => realCurrentRows[i]);
      projectedZValues = keepIndices.map((i) => realProjectedRows[i]);
      gapZValues = keepIndices.map((i) => realGapRows[i]);
    } else if (expirationDensity === 'more') {
      dteValues = buildDenseDtes(realDtes, MORE_EXPIRATION_CAP);
      currentZValues = interpolateAcrossDtes(
        realDtes,
        realCurrentRows,
        dteValues,
        strikeGrid.length,
      );
      projectedZValues = interpolateAcrossDtes(
        realDtes,
        realProjectedRows,
        dteValues,
        strikeGrid.length,
      );
      gapZValues = interpolateAcrossDtes(
        realDtes,
        realGapRows,
        dteValues,
        strikeGrid.length,
      );
    }

    // Prune sparse expirations (columns) - decided from currentZValues alone
    // so projected/gap (independently absent) never drives what's shown.
    const expPruneThreshold = PRUNE_THRESHOLDS[pruneExpirations];
    if (expPruneThreshold > 0 && dteValues.length > MIN_PRUNED_EXPIRATIONS) {
      const keepExpIndices = currentZValues
        .map((row, i) => [i, row] as const)
        .filter(([, row]) => {
          const validCount = row.filter((v) => !Number.isNaN(v)).length;
          return validCount / row.length >= expPruneThreshold;
        })
        .map(([i]) => i);
      if (keepExpIndices.length >= MIN_PRUNED_EXPIRATIONS) {
        dteValues = keepExpIndices.map((i) => dteValues[i]);
        currentZValues = keepExpIndices.map((i) => currentZValues[i]);
        projectedZValues = keepExpIndices.map((i) => projectedZValues[i]);
        gapZValues = keepExpIndices.map((i) => gapZValues[i]);
      }
    }

    // Prune sparse strikes (rows) the same way, across whichever expirations survived above.
    let finalStrikeGrid = strikeGrid;
    const strikePruneThreshold = PRUNE_THRESHOLDS[pruneStrikes];
    if (
      strikePruneThreshold > 0 &&
      finalStrikeGrid.length > MIN_PRUNED_STRIKES
    ) {
      const keepStrikeIndices = finalStrikeGrid
        .map((_, strikeIdx) => strikeIdx)
        .filter((strikeIdx) => {
          const validCount = currentZValues.filter(
            (row) => !Number.isNaN(row[strikeIdx]),
          ).length;
          return validCount / currentZValues.length >= strikePruneThreshold;
        });
      if (keepStrikeIndices.length >= MIN_PRUNED_STRIKES) {
        finalStrikeGrid = keepStrikeIndices.map((i) => finalStrikeGrid[i]);
        currentZValues = currentZValues.map((row) =>
          keepStrikeIndices.map((i) => row[i]),
        );
        projectedZValues = projectedZValues.map((row) =>
          keepStrikeIndices.map((i) => row[i]),
        );
        gapZValues = gapZValues.map((row) =>
          keepStrikeIndices.map((i) => row[i]),
        );
      }
    }

    const dateByDte = new Map(
      expirationsList.map((exp) => [exp.dte, exp.date]),
    );

    return {
      strikeGrid: finalStrikeGrid,
      dteValues,
      expirationDates: dteValues.map((dte) => dateByDte.get(dte) ?? null),
      currentZValues,
      projectedZValues,
      gapZValues,
    };
  }, [
    surfaceData,
    projectedSurface,
    strikeDensity,
    expirationDensity,
    pruneExpirations,
    pruneStrikes,
    strikeSpacing,
  ]);
